import React, { useEffect, useState } from 'react';
import { useTranslate, useGetOne } from 'react-admin';
import { Box, Button, Dialog, DialogContent, Typography, Stack, DialogTitle, DialogActions } from '@mui/material';
import PlayIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import PauseIcon from '@mui/icons-material/Pause';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useStopwatch } from 'react-timer-hook';

import CircularTimer from './CircularTimer';


const convertSecondsToHours = (value) => {
    const hours = Math.round((value / 3600) * 4) / 4;

    if (hours < 0.25) {
        return 0.25;
    }

    return hours;
};

const updateFavicon = (favicon) => {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href = favicon;
};

const Timer = ({ onStart, onStop, onPause, onReset, setIsRunning, isPaused, setIsPaused, textStart = '', textStop = '', offsetTimestamp, description, projectId, typeId, notes, ...props }) => {
    const [showResetDialog, setShowResetDialog] = useState(false);
    const [showStopDialog, setShowStopDialog] = useState(false);
    const {
        totalSeconds,
        seconds,
        minutes,
        hours,
        days,
        isRunning,
        pause,
        start,
        reset
    } = useStopwatch({ offsetTimestamp, autoStart: props.isRunning });
    const translate = useTranslate();
    const { data: project } = useGetOne('projects', { id: projectId, }, { enabled: !!projectId });
    const { data: type } = useGetOne('time_entry_types', { id: typeId, }, { enabled: !!typeId });
    const formattedHours = convertSecondsToHours(totalSeconds);
    const status = isPaused ? 'paused' : isRunning ? 'running' : 'idle';

    useEffect(() => {
        setIsRunning(isRunning);
        if (isRunning) {
            updateFavicon('/favicon-active.ico');
        }
    }, [isRunning, setIsRunning]);

    const handleStart = () => {
        if (onStart) {
            onStart();
        }

        updateFavicon('/favicon-active.ico');

        reset();
    };

    const handleStop = () => {
        if (onStop) {
            onStop(formattedHours);
        }

        updateFavicon('/favicon.ico');

        reset();
        pause();
        handleCloseStopDialog();
        setIsPaused(false);
    };

    const handlePause = () => {
        if (onPause) {
            onPause();
        }

        updateFavicon('/favicon.ico');

        pause();
        setIsPaused(true);
    };

    const handleResume = () => {
        if (onPause) {
            onPause();
        }

        updateFavicon('/favicon-active.ico');

        start();
        setIsPaused(false);
    };

    const handleReset = () => {
        if (onReset) {
            onReset();
        }
        reset();
        pause();
        handleCloseResetDialog();
        setIsPaused(false);
    };

    const handleOpenResetDialog = () => {
        setShowResetDialog(true);
    };

    const handleCloseResetDialog = () => {
        setShowResetDialog(false);
    };

    const handleOpenStopDialog = () => {
        handlePause();
        setShowStopDialog(true);
    };

    const handleCloseStopDialog = () => {
        setShowStopDialog(false);
    };

    const handleCancelStop = () => {
        handleCloseStopDialog();
        handleResume();
    };

    return (
        <Box pb={3}>
            <Stack>
                <CircularTimer
                    formattedTime={<><span>{String(days * 24 + hours).padStart(2, '0')}</span>:<span>{String(minutes).padStart(2, '0')}</span>:<span>{String(seconds).padStart(2, '0')}</span></>}
                    formattedHours={totalSeconds > 0 ? `${new Intl.NumberFormat('fr').format(formattedHours)}h` : '0h'}
                    status={status}
                    elapsedTime={totalSeconds}
                    totalSeconds={15 * 60}
                />
                <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    sx={{ mt: 2 }}
                >
                    {status === 'idle' ? (
                        <Button
                            variant="contained"
                            startIcon={<PlayIcon />}
                            onClick={handleStart}
                            sx={{
                                px: 4,
                                py: 1.2,
                                borderRadius: 2,
                                backgroundColor: '#6366F1',
                                color: '#fff',
                                fontWeight: 600,
                                textTransform: 'none',
                                fontSize: '0.9rem',
                                '&:hover': {
                                    backgroundColor: '#4F46E5',
                                },
                            }}
                        >
                            Start
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="contained"
                                startIcon={<StopIcon />}
                                onClick={handleOpenStopDialog}
                                sx={{
                                    px: 3,
                                    py: 1.2,
                                    borderRadius: 2,
                                    backgroundColor: '#6366F1',
                                    color: '#fff',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    fontSize: '0.9rem',
                                    '&:hover': {
                                        backgroundColor: '#4F46E5',
                                    },
                                }}
                            >
                                Stop
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={status === 'paused' ? <PlayIcon /> : <PauseIcon />}
                                onClick={status === 'paused' ? handleResume : handlePause}
                                sx={{
                                    px: 3,
                                    py: 1.2,
                                    borderRadius: 2,
                                    backgroundColor: '#8B5CF6',
                                    color: '#fff',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    fontSize: '0.9rem',
                                    '&:hover': {
                                        backgroundColor: '#7C3AED',
                                    },
                                }}
                            >
                                {status === 'paused' ? 'Reprendre' : 'Pause'}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={handleOpenResetDialog}
                                sx={{
                                    px: 3,
                                    py: 1.2,
                                    borderRadius: 2,
                                    borderColor: 'rgba(167, 139, 250, 0.5)',
                                    color: '#A78BFA',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    fontSize: '0.9rem',
                                    '&:hover': {
                                        borderColor: '#A78BFA',
                                        backgroundColor: 'rgba(167, 139, 250, 0.15)',
                                    },
                                }}
                            >
                                Reset
                            </Button>
                        </>
                    )}
                </Stack>
            </Stack>

            <Dialog fullWidth maxWidth="sm" open={showResetDialog} onClose={handleCloseResetDialog}>
                <DialogContent dividers sx={{ pt: 2 }}>
                    <Typography>{translate('Reset time entry?')}</Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button variant="contained" onClick={handleReset}>{translate('OK')}</Button>
                    <Button variant="outlined" onClick={handleCloseResetDialog}>{translate('ra.action.cancel')}</Button>
                </DialogActions>
            </Dialog>
            <Dialog fullWidth maxWidth="sm" open={showStopDialog} onClose={handleCancelStop}>
                <DialogTitle sx={{ pb: 1 }}>
                    {translate('Add this time entry?')}
                </DialogTitle>

                <DialogContent dividers sx={{ pt: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {description && (
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {translate('Description')}
                                </Typography>
                                <Typography variant="body1">
                                    {description}
                                </Typography>
                            </Box>
                        )}
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                                {translate('Hours')}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {formattedHours}h
                            </Typography>
                        </Box>
                        {project && (
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {translate('Project')}
                                </Typography>
                                <Typography variant="body1">
                                    {project?.name}
                                </Typography>
                            </Box>
                        )}
                        {type && (
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {translate('Type')}
                                </Typography>
                                <Typography variant="body1">
                                    {type?.name}
                                </Typography>
                            </Box>
                        )}
                        {notes && (
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {translate('Notes')}
                                </Typography>
                                <Typography variant="body1">
                                    {notes}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button variant="contained" onClick={handleStop}>
                        {translate('OK')}
                    </Button>
                    <Button variant="outlined" onClick={handleCancelStop}>
                        {translate('ra.action.cancel')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Timer;